import { INDUSTRIES, getItemsByIndustry } from "./categoryData";
import { getSubcategoryRichModule } from "../pages/guest_pages/subcategory_pages/registry";
import {
  resolveCanonicalArticleSlug,
  resolveCanonicalTopicSlug,
  topicSlugsMatch,
} from "../utils/topicSlugAliases";
import {
  getIndustryGalleryImages,
  getSubcategoryImage,
} from "./subcategoryImages";

const INDUSTRY_PROMISES = {
  "real-estate": "Verify land, compare homes, and understand the process before you pay.",
  legal: "Know your rights, documents, and next step before speaking to counsel.",
  nutrition: "Build practical meal plans around Ghanaian ingredients and real routines.",
  health: "Find the right cover, clinic, or wellness guidance with less guesswork.",
  careers: "Turn job search, upskilling, and relocation questions into a clear plan.",
  "betting-gaming":
    "Follow Ghana's real demand — football, Aviator, MoMo apps, bonuses, and laws — with guides that keep play responsible.",
};

const INDUSTRY_TONES = {
  "real-estate": "Property desk",
  legal: "Legal clarity",
  nutrition: "Food and wellness",
  health: "Care navigation",
  careers: "Career moves",
  "betting-gaming": "Odds & outcomes",
};

function buildIndustryVisual(industryId) {
  const [heroImage, secondaryImage, accentImage] = getIndustryGalleryImages(industryId);
  return {
    heroImage,
    secondaryImage,
    accentImage,
    tone: INDUSTRY_TONES[industryId] ?? "InfoBoxx",
    promise: INDUSTRY_PROMISES[industryId] ?? "Explore trusted guidance for Ghanaian consumers.",
  };
}

const INDUSTRY_VISUALS = Object.fromEntries(
  ["real-estate", "legal", "nutrition", "health", "careers", "betting-gaming"].map((id) => [
    id,
    buildIndustryVisual(id),
  ]),
);

const SPONSORS_BY_INDUSTRY = {
  "real-estate": {
    name: "GoldKey Property Desk",
    role: "Verified real estate advisor",
    action: "Request property guidance",
    logoInitials: "GK",
    consultant: "Akosua Mensah",
    responseTime: "Replies within 1 business day",
    trustLine: "Due diligence, mortgage comparisons, and buyer support.",
    phone: "+233 20 555 0181",
    email: "property@infoboxx.com",
  },
  legal: {
    name: "Afrimore Legal Desk",
    role: "Verified legal services sponsor",
    action: "Talk to a legal advisor",
    logoInitials: "AL",
    consultant: "Kwame Boateng",
    responseTime: "Initial review within 24 hours",
    trustLine: "Plain-language legal triage before formal representation.",
    phone: "+233 20 555 0182",
    email: "legal@infoboxx.com",
  },
  nutrition: {
    name: "Nourish Ghana Studio",
    role: "Meal planning and wellness sponsor",
    action: "Get a custom plan",
    logoInitials: "NG",
    consultant: "Esi Appiah",
    responseTime: "Plan review within 48 hours",
    trustLine: "Local recipes, meal planning, and habit coaching.",
    phone: "+233 20 555 0183",
    email: "nutrition@infoboxx.com",
  },
  health: {
    name: "CareBridge Health Desk",
    role: "Verified health access partner",
    action: "Book health guidance",
    logoInitials: "CH",
    consultant: "Dr. Nana Owusu",
    responseTime: "Care navigator responds same day",
    trustLine: "Insurance, diagnostics, and consultation routing.",
    phone: "+233 20 555 0184",
    email: "health@infoboxx.com",
  },
  careers: {
    name: "Pathway Careers Hub",
    role: "Jobs and skills sponsor",
    action: "Get career support",
    logoInitials: "PC",
    consultant: "Maame Ofori",
    responseTime: "Career desk replies within 24 hours",
    trustLine: "CV, applications, interview prep, and skills pathways.",
    phone: "+233 20 555 0185",
    email: "careers@infoboxx.com",
  },
  "betting-gaming": {
    name: "PlaySmart Ghana",
    role: "Responsible gaming sponsor",
    action: "Compare trusted options",
    logoInitials: "PS",
    consultant: "Yaw Ansah",
    responseTime: "Responsible gaming desk responds daily",
    trustLine: "Licensed platform comparison and safer play education.",
    phone: "+233 20 555 0186",
    email: "playsmart@infoboxx.com",
  },
};

const TYPE_ROTATION = ["Guide", "Checklist", "Expert Explainer"];

const titlePatterns = [
  (item) => `${item.name}: what to know before you start`,
  (item) => `A practical checklist for ${item.name.toLowerCase()}`,
  (item) => `Common mistakes people make with ${item.name.toLowerCase()}`,
];

const excerptPatterns = [
  (item, industry) =>
    `A clear, Ghana-focused guide that explains ${item.name.toLowerCase()} and how to make better decisions in ${industry.label.toLowerCase()}.`,
  (item) =>
    `Use this checklist to compare options, avoid rushed decisions, and know when to speak with a verified provider.`,
  (item) =>
    `Learn the warning signs, useful documents, and next steps before sharing your details or making payment.`,
];

const authorRotation = [
  "InfoBoxx Editorial Desk",
  "Partner Knowledge Team",
  "Consumer Guidance Unit",
];

const tagPatterns = [
  ["Beginner guide", "Ghana", "Checklist"],
  ["Documents", "Questions to ask", "Provider comparison"],
  ["Mistakes", "Red flags", "Next steps"],
];

const paragraphFor = (industry, item, index) => {
  const sponsor = getSponsorForIndustry(industry.id);
  const templates = [
    `${item.name} usually starts with a question, not a purchase decision. Infoboxx helps you understand the issue first, then shows the practical routes available through verified providers.`,
    `For users in Ghana, the most important step is to separate helpful guidance from sales pressure. This topic belongs under ${industry.label}, where the goal is to make the decision easier to understand before any commitment is made.`,
    `${sponsor.name} supports this topic as a service provider. Their role is to keep the knowledge base useful, answer high-intent questions, and receive qualified requests when a user is ready for help.`,
    `Before taking action, compare the available options, confirm costs, ask for timelines, and keep records of every conversation. A good provider should be able to explain the process in plain language.`,
    `When you register, Infoboxx can personalise your experience, remember the topics you care about, and simulate the next step: SMS follow-up, expert callback, WhatsApp request, or appointment booking.`,
  ];
  return templates[index % templates.length];
};

const makeTopicImage = (industryId, index = 0, slug = null) => {
  const slugImage = slug ? getSubcategoryImage(industryId, slug) : null;
  if (slugImage) return slugImage;
  const gallery = getIndustryGalleryImages(industryId);
  return gallery[index % gallery.length];
};

const makeTakeaways = (industry, item) => [
  `Start with the exact question you need answered in ${item.name.toLowerCase()}.`,
  "Compare at least two options before sharing documents or making payment.",
  `Use ${getSponsorForIndustry(industry.id).name} when you are ready for expert follow-up.`,
];

export function getIndustryVisual(industryId) {
  return INDUSTRY_VISUALS[industryId] ?? INDUSTRY_VISUALS["real-estate"];
}

/** Cover art per subcategory card on category landing pages. */
export function getSubcategoryShowcaseImage(industryId, item) {
  const rawSlug = item?.slug ?? "";
  const slug = rawSlug
    ? resolveCanonicalTopicSlug(industryId, rawSlug) || rawSlug
    : "";
  const local = getSubcategoryImage(industryId, slug);
  if (local) return local;

  const gallery = getIndustryGalleryImages(industryId);
  let slugHash = 0;
  for (let i = 0; i < slug.length; i += 1) {
    slugHash = (slugHash * 33 + slug.charCodeAt(i)) >>> 0;
  }
  const idx = ((item?.id ?? 0) * 13 + slugHash) % gallery.length;
  return gallery[idx];
}

export function getSponsorForIndustry(industryId) {
  return SPONSORS_BY_INDUSTRY[industryId] ?? {
    name: "Infoboxx Expert Desk",
    role: "Verified sponsor",
    action: "Get expert help",
    logoInitials: "IX",
    consultant: "InfoBoxx Advisor",
    responseTime: "Replies within 1 business day",
    trustLine: "Qualified support for high-intent consumer questions.",
    phone: "+233 20 555 0100",
    email: "hello@infoboxx.com",
  };
}

export function getTopicArticles(industryId, item) {
  const industry =
    INDUSTRIES.find((candidate) => candidate.id === industryId) ?? INDUSTRIES[0];
  const sponsor = getSponsorForIndustry(industryId);

  const normalizedSlug =
    item?.slug != null && item.slug !== ""
      ? resolveCanonicalTopicSlug(industryId, item.slug) || item.slug
      : item?.slug;
  const itemForArticles =
    item && normalizedSlug !== item.slug ? { ...item, slug: normalizedSlug } : item;

  const richModule = getSubcategoryRichModule(itemForArticles.slug);
  if (richModule) {
    const d = richModule.getDefaultData();
    if (Array.isArray(d.articles) && d.articles.length > 0) {
      return d.articles.map((a, index) => ({
        ...a,
        sponsor,
        updatedAt: a.updatedAt ?? a.publishedAt ?? "Reviewed recently",
        socialText: a.socialText ?? `Read this InfoBoxx guide: ${a.title}`,
        pullQuote: a.pullQuote ?? makeTakeaways(industry, itemForArticles)[0],
        takeaways: a.takeaways ?? makeTakeaways(industry, itemForArticles),
        video: a.video ?? {
          title: `${itemForArticles.name} explained in 90 seconds`,
          duration: `${2 + index}:0${index + 2}`,
          poster: a.coverImage ?? makeTopicImage(industryId, index),
        },
        body:
          a.body ??
          [
            { heading: "Why this matters", text: paragraphFor(industry, itemForArticles, 0) },
            { heading: "Context for Ghanaian consumers", text: paragraphFor(industry, itemForArticles, 1) },
            { heading: "Provider-backed guidance", text: paragraphFor(industry, itemForArticles, 2) },
            {
              heading: "Quick decision checklist",
              text: paragraphFor(industry, itemForArticles, 3),
              bullets: [
                "Confirm the provider's identity and credentials.",
                "Ask for fees, timelines, documents, and follow-up channels.",
                "Keep screenshots, receipts, emails, and signed documents.",
              ],
            },
            { heading: "What happens after registration", text: paragraphFor(industry, itemForArticles, 4) },
          ],
      }));
    }
  }

  return titlePatterns.map((makeTitle, index) => {
    const title = makeTitle(itemForArticles);
    return {
      id: `${itemForArticles.slug}-${index + 1}`,
      slug: `${itemForArticles.slug}-${index + 1}`,
      title,
      excerpt: excerptPatterns[index](itemForArticles, industry),
      type: TYPE_ROTATION[index],
      readTime: `${4 + index} min read`,
      sponsor,
      author: authorRotation[index],
      publishedAt: ["Today", "This week", "Updated recently"][index],
      updatedAt: ["Just refreshed", "Reviewed this month", "Partner verified"][index],
      coverImage: makeTopicImage(industryId, index),
      tags: tagPatterns[index],
      socialText: `Read this InfoBoxx guide: ${title}`,
      pullQuote: `The best ${itemForArticles.name.toLowerCase()} decisions start with clarity, not pressure.`,
      takeaways: makeTakeaways(industry, itemForArticles),
      video: {
        title: `${itemForArticles.name} explained in 90 seconds`,
        duration: `${2 + index}:0${index + 2}`,
        poster: makeTopicImage(industryId, index + 1),
      },
      body: [
        {
          heading: "Why this matters",
          text: paragraphFor(industry, itemForArticles, 0),
        },
        {
          heading: "Context for Ghanaian consumers",
          text: paragraphFor(industry, itemForArticles, 1),
        },
        {
          heading: "Provider-backed guidance",
          text: paragraphFor(industry, itemForArticles, 2),
        },
        {
          heading: "Quick decision checklist",
          text: paragraphFor(industry, itemForArticles, 3),
          bullets: [
            "Confirm the provider's identity and credentials.",
            "Ask for fees, timelines, documents, and follow-up channels.",
            "Keep screenshots, receipts, emails, and signed documents.",
          ],
        },
        {
          heading: "What happens after registration",
          text: paragraphFor(industry, itemForArticles, 4),
        },
      ],
    };
  });
}

export function getTopicExperience(industryId, item) {
  const industry =
    INDUSTRIES.find((candidate) => candidate.id === industryId) ?? INDUSTRIES[0];
  const sponsor = getSponsorForIndustry(industryId);
  const visual = getIndustryVisual(industryId);

  return {
    eyebrow: `${visual.tone} / ${item.name}`,
    heroImage: makeTopicImage(industryId, item.id, item.slug),
    gallery: [
      makeTopicImage(industryId, item.id, item.slug),
      makeTopicImage(industryId, item.id + 1, item.slug),
      makeTopicImage(industryId, item.id + 2, item.slug),
    ],
    stats: [
      { label: "Guides", value: "3+" },
      { label: "Response", value: sponsor.responseTime.split(" ").slice(0, 2).join(" ") },
      { label: "Topic", value: industry.label.split(" ")[0] },
    ],
    modules: [
      {
        title: "What consumers can learn here",
        text: `A practical overview of ${item.name.toLowerCase()}, built to answer common questions before a user speaks to a provider.`,
      },
      {
        title: "Key things to check",
        text: "Compare options, confirm costs and timelines, and keep records of every conversation before you commit.",
      },
      {
        title: "How Infoboxx converts interest",
        text: `Consumers can read, watch, register, and request help from ${sponsor.name} when they are ready for the next step.`,
      },
    ],
    video: {
      title: `${item.name}: provider introduction`,
      duration: "01:45",
      poster: visual.secondaryImage,
    },
  };
}

export function getTopicBySlug(industryId, slug) {
  const key = slug ? resolveCanonicalTopicSlug(industryId, slug) || String(slug).trim() : "";
  if (!key) return null;
  return getItemsByIndustry(industryId).find((item) => item.slug === key) ?? null;
}

export function getArticleBySlug(industryId, topicSlug, articleSlug) {
  const topic = getTopicBySlug(industryId, topicSlug);
  if (!topic) return null;
  const articleKey = resolveCanonicalArticleSlug(industryId, topicSlug, articleSlug);
  return (
    getTopicArticles(industryId, topic).find(
      (article) => article.slug === articleKey || topicSlugsMatch(article.slug, articleKey),
    ) ?? null
  );
}

export function getRelatedArticles(industryId, topicSlug, currentSlug) {
  const topic = getTopicBySlug(industryId, topicSlug);
  if (!topic) return [];

  const sameTopic = getTopicArticles(industryId, topic).filter(
    (article) => !topicSlugsMatch(article.slug, currentSlug ?? ""),
  );
  if (sameTopic.length >= 2) return sameTopic;

  const relatedTopics = getItemsByIndustry(industryId)
    .filter((item) => item.slug !== topic.slug)
    .slice(0, 3);

  return [
    ...sameTopic,
    ...relatedTopics.map((item) => getTopicArticles(industryId, item)[0]),
  ].filter(Boolean).slice(0, 3);
}
